import { useEffect, useState, useCallback } from "react";
import AnalyticsQuery from "./AnalyticsQuery";
import OddsAnalysis from "./OddsAnalysis";
import { ALL_LEAGUES } from "../constants";
import { useIsMobile } from "../useIsMobile";
import { MOT_DATA, MKT_DATA, DONEM_DATA, TAKIM_POS, TAKIM_NEG } from "../data/kalibrasyon";

const API_URL = process.env.REACT_APP_API_URL || "https://web-production-c0e77.up.railway.app";

// ─── Renk sabitleri ───────────────────────────────────────────────────────────
const C = {
  home: "#16a34a", draw: "#d97706", away: "#2563eb",
  bg: "#f9fafb", card: "#ffffff", border: "#e5e7eb",
  muted: "#9ca3af", text: "#111827", sub: "#6b7280",
};

// ─── Yardımcı bileşenler ──────────────────────────────────────────────────────
function Pill({ color, children }) {
  return (
    <span style={{
      background: `${color}18`, color, border: `1px solid ${color}40`,
      borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 700,
    }}>{children}</span>
  );
}

function StatBox({ label, value, sub, color }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderTop: `3px solid ${color || C.border}`,
      borderRadius: 6, padding: "16px 20px", flex: 1, minWidth: 130,
    }}>
      <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || C.text, lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: C.sub, marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

function StackedBar({ homePct, drawPct, awayPct, height = 24, showLabels = true }) {
  return (
    <div style={{ display: "flex", height, borderRadius: 3, overflow: "hidden", gap: 1 }}>
      {[
        { pct: homePct, color: C.home },
        { pct: drawPct, color: C.draw },
        { pct: awayPct, color: C.away },
      ].map(({ pct, color }, i) => (
        <div key={i} style={{ width: `${pct}%`, background: color,
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          {showLabels && pct >= 8 && (
            <span style={{ fontSize: 9, fontWeight: 700, color: "#fff" }}>{pct}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: C.text }}>
      {children}
    </div>
  );
}

function Card({ children, mb = 20 }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 8, padding: "20px 24px", marginBottom: mb }}>
      {children}
    </div>
  );
}

// ─── Ana Bileşen ──────────────────────────────────────────────────────────────
export default function Statistics({ initialTab }) {
  const isMobile = useIsMobile();
  const [baseData, setBaseData]   = useState(null);  // tüm ligler (cache'li)
  const [ligData, setLigData]     = useState(null);  // seçili lig
  const [selLeague, setSelLeague] = useState("");    // "" = tüm ligler
  const [loading, setLoading]     = useState(true);
  const [ligLoading, setLigLoading] = useState(false);
  const [error, setError]         = useState(null);
  const [sortBy, setSortBy]       = useState("ber");
  const [statsTab, setStatsTab]   = useState(initialTab || "istatistik"); // "istatistik" | "sorgu" | "kalibrasyon"
  // Ana sayfadan belirli bir sekmeye yönlendirme: sayfa hep mount'lu
  // (display:none) olduğu için prop değişince state'i elle senkronla.
  useEffect(() => { if (initialTab) setStatsTab(initialTab); }, [initialTab]);

  // Tüm ligler verisini yükle (bir kez)
  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/league-stats`)
      .then(r => r.json())
      .then(d => { setBaseData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  // Lig seçilince o ligin detayını çek
  const fetchLeague = useCallback((lg) => {
    if (!lg) { setLigData(null); return; }
    setLigLoading(true);
    fetch(`${API_URL}/api/league-stats?league=${encodeURIComponent(lg)}`)
      .then(r => r.json())
      .then(d => { setLigData(d); setLigLoading(false); })
      .catch(() => setLigLoading(false));
  }, []);

  const handleLeagueChange = (lg) => {
    setSelLeague(lg);
    fetchLeague(lg);
  };

  if (loading) return <div className="loading">İstatistikler yükleniyor…</div>;
  if (error)   return <div className="empty" style={{ color: "#ef4444" }}>Hata: {error}</div>;
  if (!baseData) return <div className="empty">Veri bulunamadı.</div>;

  // Aktif veri — lig seçiliyse ligData, yoksa baseData
  const data = (selLeague && ligData) ? ligData : baseData;
  const { overall, by_league, season_trend, top_scores, goal_dist, total_matches } = data;

  const leagueList = Object.entries(by_league || {}).sort((a, b) => {
    if (sortBy === "ber") return b[1].draw_pct - a[1].draw_pct;
    if (sortBy === "ev")  return b[1].home_pct - a[1].home_pct;
    if (sortBy === "dep") return b[1].away_pct - a[1].away_pct;
    if (sortBy === "gol") return b[1].avg_goals - a[1].avg_goals;
    if (sortBy === "n")   return b[1].n - a[1].n;
    return 0;
  });

  const available = Object.keys(baseData?.by_league || {});
  const allLeagues = [
    ...ALL_LEAGUES.filter(l => available.includes(l)),
    ...available.filter(l => !ALL_LEAGUES.includes(l)).sort(),
  ];

  return (
    <div className="main">
      {/* ── Başlık + Filtre ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 16, flexShrink: 0,
        padding: "10px 20px", borderBottom: `1px solid ${C.border}`, background: "#fff",
      }}>
        <select
          value={selLeague}
          onChange={e => handleLeagueChange(e.target.value)}
          style={{
            fontSize: 13, padding: "5px 12px", flexShrink: 0, width: 200,
            border: `1px solid ${ligLoading ? C.draw : selLeague ? C.away : C.border}`,
            borderRadius: 6, background: "#fff", cursor: "pointer",
            color: selLeague ? C.away : C.text, fontWeight: selLeague ? 600 : 400,
            opacity: ligLoading ? 0.7 : 1,
          }}
        >
          <option value="">🌍 Tüm Ligler</option>
          {allLeagues.map(lg => <option key={lg} value={lg}>{lg}</option>)}
        </select>
        <b style={{ fontSize: 15, flexShrink: 0 }}>İstatistikler</b>
        <span style={{ fontSize: 13, color: "#aaa", flexShrink: 0 }}>
          {total_matches?.toLocaleString("tr-TR")} maç
          {!selLeague && ` · ${Object.keys(baseData?.by_league || {}).length} lig`}
        </span>
        <span style={{ fontSize: 11, color: "#bbb" }}>
          {baseData?.generated_at
            ? `Son güncelleme: ${new Date(baseData.generated_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}`
            : ""}
        </span>
      </div>

      {/* ── Tab navigasyonu ── */}
      <div className="m-body">
        <div style={{ display: "flex", gap: 4, borderBottom: `2px solid ${C.border}`, marginBottom: 24 }}>
          {[
            ["istatistik", "📊 İstatistikler"],
            ["sorgu",      "🎛 Sorgula"],
            ["odds",       "📈 Piyasa Sapması"],
            ["kalibrasyon","🔧 Kalibrasyon"],
          ].map(([key, lbl]) => (
            <button key={key} onClick={() => setStatsTab(key)} style={{
              padding: "8px 18px", fontSize: 13, fontWeight: statsTab === key ? 700 : 500,
              border: "none", borderBottom: statsTab === key ? `2px solid ${C.text}` : "2px solid transparent",
              background: "transparent", cursor: "pointer",
              color: statsTab === key ? C.text : C.sub, marginBottom: -2,
            }}>{lbl}</button>
          ))}
        </div>

        {/* ════ TAB: İSTATİSTİKLER ════ */}
        {statsTab === "istatistik" && (
          <div>
            <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
              <StatBox label="Ev Sahibi Galibiyeti"
                value={`${overall?.home_pct}%`}
                sub={`Ort. ${overall?.avg_home_goals} gol/maç`} color={C.home} />
              <StatBox label="Beraberlik"
                value={`${overall?.draw_pct}%`}
                sub={`0-0 oranı: %${overall?.zero_zero_pct}`} color={C.draw} />
              <StatBox label="Deplasman Galibiyeti"
                value={`${overall?.away_pct}%`}
                sub={`Ort. ${overall?.avg_away_goals} gol/maç`} color={C.away} />
              <StatBox label="Maç Başı Ort. Gol"
                value={overall?.avg_goals}
                sub={`Ev ${overall?.avg_home_goals} / Dep ${overall?.avg_away_goals}`}
                color="#7c3aed" />
            </div>

            {/* Legend */}
            <div style={{ display: "flex", gap: 16, marginBottom: 24, fontSize: 12 }}>
              {[["Ev Sahibi", C.home], ["Beraberlik", C.draw], ["Deplasman", C.away]].map(([lbl, col]) => (
                <span key={lbl} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 12, height: 12, background: col, borderRadius: 2, display: "inline-block" }} />
                  <span style={{ color: col, fontWeight: 600 }}>{lbl}</span>
                </span>
              ))}
            </div>

            {/* ── Gol Dağılımı ── */}
            {(goal_dist || []).length > 0 && (
              <Card>
                <SectionTitle>Toplam Gol Sayısına Göre Maç Dağılımı</SectionTitle>
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
                  {(goal_dist || []).map(g => {
                    const maxPct = Math.max(...(goal_dist).map(x => x.pct));
                    const h = Math.max(40, (g.pct / maxPct) * 160);
                    const barColor = g.goals === 0 ? "#e5e7eb" : g.goals === 2 ? "#fef3c7" :
                      (g.goals === 1 || g.goals === 3) ? "#d1fae5" : "#dbeafe";
                    return (
                      <div key={g.goals} style={{ textAlign: "center", flex: 1, minWidth: 44 }}>
                        <div style={{ fontSize: 10, color: C.sub, marginBottom: 4 }}>{g.pct}%</div>
                        <div style={{ height: h, background: barColor, borderRadius: "3px 3px 0 0",
                          border: `1px solid ${C.border}` }} />
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginTop: 4 }}>{g.goals}</div>
                        <div style={{ fontSize: 10, color: C.muted }}>gol</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>
                  En sık: <strong>{goal_dist?.find(g => g.goals === 2)?.pct}% (2 gol)</strong>,
                  ardından <strong>{goal_dist?.find(g => g.goals === 3)?.pct}% (3 gol)</strong>.
                  Maçların <strong>%{((goal_dist?.filter(g => Number(g.goals) <= 2) || []).reduce((s, g) => s + g.pct, 0)).toFixed(1)}</strong>'i 2 veya daha az golle bitiyor.
                </div>
                {/* Gol sayısına göre 1-X-2 */}
                <SectionTitle>Gol Sayısına Göre 1-X-2 Olasılığı</SectionTitle>
                {(goal_dist || []).map(g => (
                  <div key={g.goals} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      fontSize: 12, color: C.sub, marginBottom: 4 }}>
                      <span><strong style={{ color: C.text }}>{g.goals} gol</strong> ({g.pct}% olasılık)</span>
                      <span style={{ display: "flex", gap: 12 }}>
                        <span style={{ color: C.home }}>Ev {g.home_pct}%</span>
                        <span style={{ color: C.draw }}>Ber {g.draw_pct}%</span>
                        <span style={{ color: C.away }}>Dep {g.away_pct}%</span>
                      </span>
                    </div>
                    <StackedBar homePct={g.home_pct} drawPct={g.draw_pct} awayPct={g.away_pct} />
                  </div>
                ))}
              </Card>
            )}

            {/* ── Sezon Trendi ── */}
            {(season_trend || []).length > 1 && (
              <Card>
                <SectionTitle>Sezon Bazında Trend{selLeague ? ` — ${selLeague}` : ""}</SectionTitle>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: C.bg }}>
                        {["Sezon", "N", "Ev%", "Ber%", "Dep%", "Ort.Gol", "1-X-2"].map(h => (
                          <th key={h} style={{ padding: "8px 12px", textAlign: "left",
                            color: C.muted, fontSize: 11, letterSpacing: 1,
                            textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(season_trend || []).map(s => (
                        <tr key={s.season} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: "9px 12px", fontWeight: 700 }}>{s.season}</td>
                          <td style={{ padding: "9px 12px", color: C.sub }}>{s.n.toLocaleString("tr-TR")}</td>
                          <td style={{ padding: "9px 12px", color: C.home, fontWeight: 600 }}>{s.home_pct}%</td>
                          <td style={{ padding: "9px 12px", color: C.draw, fontWeight: 600 }}>{s.draw_pct}%</td>
                          <td style={{ padding: "9px 12px", color: C.away, fontWeight: 600 }}>{s.away_pct}%</td>
                          <td style={{ padding: "9px 12px" }}>{s.avg_goals}</td>
                          <td style={{ padding: "9px 12px", minWidth: 160 }}>
                            <StackedBar homePct={s.home_pct} drawPct={s.draw_pct} awayPct={s.away_pct} height={20} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* ── Skorlar ── */}
            {(top_scores || []).length > 0 && (
              <Card>
                <SectionTitle>En Sık Skorlar{selLeague ? ` — ${selLeague}` : ""}</SectionTitle>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>
                  {total_matches?.toLocaleString("tr-TR")} maçta gözlemlenen skor dağılımı
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 24 }}>
                  {/* Tüm skorlar */}
                  <div>
                    {(top_scores || []).map((s, i) => {
                      const [hg, ag] = s.score.split("-").map(Number);
                      const barColor = hg > ag ? C.home : hg === ag ? C.draw : C.away;
                      const maxPct = top_scores[0]?.pct || 1;
                      return (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 7 }}>
                          <div style={{ width: 36, textAlign: "right", fontFamily: "monospace",
                            fontWeight: 800, fontSize: 15, color: barColor }}>{s.score}</div>
                          <div style={{ width: 44, flexShrink: 0 }}>
                            {hg > ag && <Pill color={C.home}>Ev</Pill>}
                            {hg === ag && <Pill color={C.draw}>Ber</Pill>}
                            {hg < ag && <Pill color={C.away}>Dep</Pill>}
                          </div>
                          <div style={{ flex: 1, background: "#f3f4f6", borderRadius: 2, height: 20, position: "relative" }}>
                            <div style={{ width: `${(s.pct / maxPct) * 100}%`, background: barColor,
                              height: "100%", borderRadius: 2, minWidth: 28,
                              display: "flex", alignItems: "center", padding: "0 7px" }}>
                              <span style={{ fontSize: 10, fontWeight: 700, color: "#fff" }}>{s.pct}%</span>
                            </div>
                          </div>
                          <div style={{ width: 55, textAlign: "right", fontSize: 12, color: C.sub }}>
                            {s.n.toLocaleString("tr-TR")}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Beraberlik skorları */}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: C.draw }}>
                      🤝 Beraberlik Skor Dağılımı
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {(top_scores || []).filter(s => {
                        const [h, a] = s.score.split("-").map(Number); return h === a;
                      }).map(s => (
                        <div key={s.score} style={{
                          background: "#fef3c740", border: `1px solid ${C.draw}40`,
                          borderRadius: 8, padding: "10px 16px", textAlign: "center", minWidth: 72,
                        }}>
                          <div style={{ fontFamily: "monospace", fontSize: 22, fontWeight: 800, color: C.draw }}>{s.score}</div>
                          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 3 }}>{s.pct}%</div>
                          <div style={{ fontSize: 11, color: C.muted }}>{s.n.toLocaleString("tr-TR")} maç</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 12 }}>
                      1-1 en yaygın beraberlik — tüm maçların ~%12'si.
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* ── Ligler Tablosu — sadece "Tüm Ligler" seçiliyken ── */}
            {!selLeague && (
              <Card mb={0}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                  marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
                  <SectionTitle style={{ marginBottom: 0 }}>Ligler Karşılaştırması</SectionTitle>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, color: C.muted, alignSelf: "center" }}>Sırala:</span>
                    {[["ber","Beraberlik%"],["ev","Ev%"],["dep","Dep%"],["gol","Ort.Gol"],["n","Maç"]].map(([key, lbl]) => (
                      <button key={key} onClick={() => setSortBy(key)} style={{
                        padding: "4px 10px", fontSize: 12, borderRadius: 4, cursor: "pointer",
                        border: `1px solid ${sortBy === key ? C.text : C.border}`,
                        background: sortBy === key ? C.text : "#fff",
                        color: sortBy === key ? "#fff" : C.sub,
                        fontWeight: sortBy === key ? 700 : 400,
                      }}>{lbl}</button>
                    ))}
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: C.bg }}>
                        {["Lig","N","Ev%","Ber%","Dep%","Ort.Gol","Ev Gol","Dep Gol","0-0%","1-X-2"].map(h => (
                          <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 10,
                            letterSpacing: 1, textTransform: "uppercase", color: C.muted,
                            borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {leagueList.map(([lg, s]) => (
                        <tr key={lg} style={{ borderBottom: `1px solid ${C.border}`,
                          cursor: "pointer", transition: "background 0.1s" }}
                          onClick={() => handleLeagueChange(lg)}
                          onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"}
                          onMouseLeave={e => e.currentTarget.style.background = ""}>
                          <td style={{ padding: "9px 12px", fontWeight: 600, color: C.away, whiteSpace: "nowrap" }}>
                            {lg}
                          </td>
                          <td style={{ padding: "9px 12px", color: C.sub }}>{s.n.toLocaleString("tr-TR")}</td>
                          <td style={{ padding: "9px 12px", color: C.home, fontWeight: sortBy === "ev" ? 800 : 500 }}>{s.home_pct}%</td>
                          <td style={{ padding: "9px 12px", color: C.draw, fontWeight: sortBy === "ber" ? 800 : 500 }}>{s.draw_pct}%</td>
                          <td style={{ padding: "9px 12px", color: C.away, fontWeight: sortBy === "dep" ? 800 : 500 }}>{s.away_pct}%</td>
                          <td style={{ padding: "9px 12px", fontWeight: sortBy === "gol" ? 700 : 400 }}>{s.avg_goals}</td>
                          <td style={{ padding: "9px 12px", color: C.sub }}>{s.avg_home_goals}</td>
                          <td style={{ padding: "9px 12px", color: C.sub }}>{s.avg_away_goals}</td>
                          <td style={{ padding: "9px 12px", color: s.zero_zero_pct >= 9 ? C.draw : C.sub }}>{s.zero_zero_pct}%</td>
                          <td style={{ padding: "9px 12px", minWidth: 100 }}>
                            <StackedBar homePct={s.home_pct} drawPct={s.draw_pct} awayPct={s.away_pct} height={16} showLabels={false} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>
                  💡 Bir lig satırına tıklayarak o ligin detaylarını filtrele.
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ════ TAB: SORGULA ════ */}
        {statsTab === "sorgu" && (
          <AnalyticsQuery
            leagues={Object.keys(baseData?.by_league || {})}
            byLeague={baseData?.by_league}
            selectedLeague={selLeague}
          />
        )}

        {/* ════ TAB: KALİBRASYON ════ */}
        {statsTab === "kalibrasyon" && <KalibrasyonSekmesi byLeague={baseData?.by_league || {}} />}

        {/* ════ TAB: PİYASA SAPMASI ════ */}
        {statsTab === "odds" && <OddsAnalysis />}
      </div>
    </div>
  );
}

// ─── Kalibrasyon Sekmesi ────────────────────────────────────────────────────
// ─── Kalibrasyon Sekmesi ────────────────────────────────────────────────────
function KalibrasyonSekmesi({ byLeague = {} }) {
  const isMobile = useIsMobile();
  const [ksTab, setKsTab] = useState("baseline");
  const [motLig, setMotLig] = useState("Süper Lig");
  const [copied, setCopied] = useState(false);

  const s = {
    card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", marginBottom: 14 },
    cardHd: { padding: "9px 14px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" },
    th: { padding: "7px 12px", background: "#f9fafb", color: "#6b7280", fontSize: 12, fontWeight: 500, textAlign: "left", borderBottom: "1px solid #e5e7eb" },
    td: { padding: "7px 12px", fontSize: 13, borderTop: "1px solid #f3f4f6" },
    tabBtn: (active) => ({ padding: "7px 16px", fontSize: 13, fontWeight: active ? 600 : 500, border: "none", borderBottom: active ? "2px solid #111" : "2px solid transparent", background: "transparent", cursor: "pointer", color: active ? "#111" : "#6b7280", marginBottom: -2 }),
    ligBtn: (active) => ({ padding: "4px 11px", borderRadius: 20, fontSize: 12, border: "1px solid " + (active ? "#9ca3af" : "#e5e7eb"), background: active ? "#f3f4f6" : "#fff", color: active ? "#111" : "#6b7280", cursor: "pointer", fontWeight: active ? 500 : 400 }),
  };

  const badge = (n, big) => {
    const abs = Math.abs(n);
    if (abs < 2) return <span style={{ color: "#9ca3af" }}>{n > 0 ? "+" : ""}{n}pp</span>;
    const col = n > 0 ? (abs >= 10 ? { bg: "#fffbeb", txt: "#92400e" } : { bg: "#f0fdf4", txt: "#166534" })
                      : { bg: "#fef2f2", txt: "#991b1b" };
    return <span style={{ background: col.bg, color: col.txt, padding: "2px 7px", borderRadius: 4, fontWeight: 500, fontSize: 12 }}>{n > 0 ? "+" : ""}{n}pp</span>;
  };

  const badgeTxt = (v) => {
    if (v === "—") return <span style={{ color: "#9ca3af" }}>—</span>;
    const n = parseFloat(v);
    if (isNaN(n) || Math.abs(n) < 2) return <span style={{ color: "#9ca3af" }}>{v}</span>;
    const abs = Math.abs(n);
    const col = n > 0 ? (abs >= 7 ? { bg: "#fffbeb", txt: "#92400e" } : { bg: "#f0fdf4", txt: "#166534" })
                      : { bg: "#fef2f2", txt: "#991b1b" };
    return <span style={{ background: col.bg, color: col.txt, padding: "2px 7px", borderRadius: 4, fontWeight: 500, fontSize: 12 }}>{v}</span>;
  };

  const MOT_LIGS = Object.keys(MOT_DATA);

  const MotTable = ({ rows, title }) => (
    <div>
      <div style={{ fontSize: 13, color: "#6b7280", margin: "14px 0 7px" }}>{title}</div>
      <div style={s.card}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed" }}>
            <thead>
              <tr>
                {["Sezon sonu durumu","Maç sayısı","Gerçek ev%","Piyasa ev%","Fark","Gerçek dep%","Piyasa dep%","Fark"].map((h, i) => (
                  <th key={i} style={{ ...s.th, width: ["26%","8%","11%","11%","10%","11%","11%","12%"][i] }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const dh = +(r.rh - r.mh).toFixed(1);
                const da = +(r.ra - r.ma).toFixed(1);
                return (
                  <tr key={i} style={{ background: i % 2 === 1 ? "#f9fafb" : "#fff" }}>
                    <td style={{ ...s.td, fontWeight: 500 }}>{r.s}</td>
                    <td style={{ ...s.td, color: "#6b7280" }}>{r.n}</td>
                    <td style={s.td}>{r.rh.toFixed(1)}%</td>
                    <td style={{ ...s.td, color: "#6b7280" }}>{r.mh.toFixed(1)}%</td>
                    <td style={s.td}>{badge(dh)}</td>
                    <td style={s.td}>{r.ra.toFixed(1)}%</td>
                    <td style={{ ...s.td, color: "#6b7280" }}>{r.ma.toFixed(1)}%</td>
                    <td style={s.td}>{badge(da)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const TABS = [
    ["baseline",   "📊 Baseline Analizi"],
    ["motivasyon", "Motivasyon"],
    ["market",     "Market Edge"],
    ["donem",      "Dönem / Midweek"],
    ["takim",      "Takım Sapmaları"],
  ];

  return (
    <div>
      <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>
        Kaynak: 8 lig · 4 sezon (2020-21, 2022-23, 2024-25, 2025-26) · son ~10 hafta · Fark = Gerçek% - Piyasa%
      </div>

      {/* Sekme nav */}
      <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #e5e7eb", marginBottom: 20 }}>
        {TABS.map(([key, lbl]) => (
          <button key={key} onClick={() => setKsTab(key)} style={s.tabBtn(ksTab === key)}>{lbl}</button>
        ))}
      </div>

      {/* ════ Baseline Analizi ════ */}
      {ksTab === "baseline" && (() => {
        // Model baseline sabitleri
        const MODEL_BASELINE = {
          "Premier League":        { ev: 44, ber: 24, dep: 32, avg_g: 2.96, draw_th: 24 },
          "Championship":          { ev: 43, ber: 26, dep: 31, avg_g: 2.53, draw_th: 26 },
          "La Liga":               { ev: 46, ber: 26, dep: 28, avg_g: 2.61, draw_th: 26 },
          "Segunda Division":      { ev: 45, ber: 30, dep: 25, avg_g: 2.33, draw_th: 30 },
          "Bundesliga":            { ev: 43, ber: 25, dep: 32, avg_g: 3.18, draw_th: 25 },
          "2. Bundesliga":         { ev: 44, ber: 25, dep: 31, avg_g: 3.00, draw_th: 25 },
          "Serie A":               { ev: 41, ber: 28, dep: 31, avg_g: 2.55, draw_th: 28 },
          "Serie B":               { ev: 41, ber: 32, dep: 27, avg_g: 2.46, draw_th: 32 },
          "Ligue 1":               { ev: 44, ber: 24, dep: 32, avg_g: 2.82, draw_th: 24 },
          "Ligue 2":               { ev: 43, ber: 28, dep: 29, avg_g: 2.47, draw_th: 28 },
          "Eredivisie":            { ev: 45, ber: 25, dep: 30, avg_g: 3.11, draw_th: 25 },
          "Pro League":            { ev: 43, ber: 26, dep: 31, avg_g: 2.83, draw_th: 26 },
          "Primeira Liga":         { ev: 44, ber: 24, dep: 32, avg_g: 2.66, draw_th: 24 },
          "Scottish Premiership":  { ev: 46, ber: 23, dep: 31, avg_g: 2.86, draw_th: 23 },
          "Champions League":      { ev: 48, ber: 19, dep: 33, avg_g: 3.05, draw_th: 19 },
          "Süper Lig":             { ev: 45, ber: 25, dep: 30, avg_g: 2.86, draw_th: 25 },
          "Super League Greece":   { ev: 42, ber: 26, dep: 32, avg_g: 2.57, draw_th: 26 },
          "Russian Premier League":{ ev: 46, ber: 26, dep: 28, avg_g: 2.75, draw_th: 26 },
          "Allsvenskan":           { ev: 44, ber: 22, dep: 34, avg_g: 2.80, draw_th: 22 },
          "Eliteserien":           { ev: 47, ber: 21, dep: 32, avg_g: 3.09, draw_th: 21 },
          "Austrian Bundesliga":   { ev: 40, ber: 27, dep: 33, avg_g: 2.75, draw_th: 27 },
          "Swiss Super League":    { ev: 44, ber: 27, dep: 29, avg_g: 3.00, draw_th: 27 },
          "Superliga":             { ev: 42, ber: 26, dep: 32, avg_g: 2.93, draw_th: 26 },
        };

        // Gerçek veri (Statistics API) ile karşılaştır
        const rows = Object.entries(MODEL_BASELINE).map(([league, model]) => {
          const real = byLeague[league];
          if (!real) return null;
          const dEv  = +(real.home_pct - model.ev).toFixed(1);
          const dBer = +(real.draw_pct - model.ber).toFixed(1);
          const dDep = +(real.away_pct - model.dep).toFixed(1);
          const dGol = +(real.avg_goals - model.avg_g).toFixed(2);
          const maxDiff = Math.max(Math.abs(dEv), Math.abs(dBer), Math.abs(dDep));
          const needsUpdate = maxDiff >= 2;
          return { league, model, real, dEv, dBer, dDep, dGol, maxDiff, needsUpdate };
        }).filter(Boolean).sort((a, b) => b.maxDiff - a.maxDiff);

        const updateNeeded = rows.filter(r => r.needsUpdate);

        // Config satırı üreteci
        const generateConfigLine = (r) => {
          const newEv  = Math.round(r.real.home_pct);
          const newBer = Math.round(r.real.draw_pct);
          const newDep = Math.round(r.real.away_pct);
          const newGol = r.real.avg_goals;
          return `"${r.league}": {"ev": ${newEv}, "ber": ${newBer}, "dep": ${newDep}, "avg_g": ${newGol}, "draw_th": ${newBer}},`;
        };

        const diffBadge = (val) => {
          const abs = Math.abs(val);
          if (abs < 1) return <span style={{ color: "#9ca3af" }}>{val > 0 ? "+" : ""}{val}</span>;
          const col = abs >= 3
            ? (val > 0 ? { bg: "#fffbeb", txt: "#92400e" } : { bg: "#fef2f2", txt: "#991b1b" })
            : (val > 0 ? { bg: "#f0fdf4", txt: "#166534" } : { bg: "#eff6ff", txt: "#1d4ed8" });
          return <span style={{ background: col.bg, color: col.txt, padding: "2px 6px", borderRadius: 4, fontWeight: 600, fontSize: 11 }}>{val > 0 ? "+" : ""}{val}</span>;
        };

        return (
          <div>
            {/* Özet banner */}
            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 16px", fontSize: 13 }}>
                <span style={{ color: "#6b7280" }}>Toplam lig: </span><strong>{rows.length}</strong>
              </div>
              <div style={{ background: updateNeeded.length > 0 ? "#fef2f2" : "#f0fdf4", border: `1px solid ${updateNeeded.length > 0 ? "#fca5a5" : "#86efac"}`, borderRadius: 6, padding: "8px 16px", fontSize: 13 }}>
                <span style={{ color: "#6b7280" }}>Güncelleme önerilen: </span>
                <strong style={{ color: updateNeeded.length > 0 ? "#dc2626" : "#16a34a" }}>{updateNeeded.length}</strong>
              </div>
              <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, padding: "8px 16px", fontSize: 12, color: "#1d4ed8" }}>
                📊 Veri kaynağı: {Object.values(byLeague).reduce((s, v) => s + v.n, 0).toLocaleString("tr-TR")} maç (son 6 sezon)
              </div>
            </div>

            {/* Ana karşılaştırma tablosu */}
            <div style={s.card}>
              <div style={s.cardHd}>
                <span>Model Baseline vs Gerçek İstatistik</span>
                <span style={{ fontSize: 11, color: "#6b7280" }}>|Δ| ≥ 3pp = güncelleme önerilir</span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      <th style={{ ...s.th, width: "18%" }}>Lig</th>
                      <th style={{ ...s.th, width: "6%", textAlign: "center" }}>N</th>
                      <th style={{ ...s.th, width: "7%", textAlign: "center", color: "#16a34a" }}>Ev%</th>
                      <th style={{ ...s.th, width: "7%", textAlign: "center", color: "#9ca3af" }}>Model</th>
                      <th style={{ ...s.th, width: "7%", textAlign: "center" }}>Δ</th>
                      <th style={{ ...s.th, width: "7%", textAlign: "center", color: "#d97706" }}>Ber%</th>
                      <th style={{ ...s.th, width: "7%", textAlign: "center", color: "#9ca3af" }}>Model</th>
                      <th style={{ ...s.th, width: "7%", textAlign: "center" }}>Δ</th>
                      <th style={{ ...s.th, width: "7%", textAlign: "center", color: "#2563eb" }}>Dep%</th>
                      <th style={{ ...s.th, width: "7%", textAlign: "center", color: "#9ca3af" }}>Model</th>
                      <th style={{ ...s.th, width: "7%", textAlign: "center" }}>Δ</th>
                      <th style={{ ...s.th, width: "7%", textAlign: "center" }}>Ort.Gol Δ</th>
                      <th style={{ ...s.th, width: "8%", textAlign: "center" }}>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.league} style={{ background: r.needsUpdate ? "#fffbeb" : i % 2 === 1 ? "#f9fafb" : "#fff", borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ ...s.td, fontWeight: 600 }}>{r.league}</td>
                        <td style={{ ...s.td, color: "#9ca3af", textAlign: "center", fontSize: 11 }}>{r.real.n?.toLocaleString("tr-TR")}</td>
                        <td style={{ ...s.td, textAlign: "center", color: "#16a34a", fontWeight: 500 }}>{r.real.home_pct}%</td>
                        <td style={{ ...s.td, textAlign: "center", color: "#9ca3af" }}>{r.model.ev}%</td>
                        <td style={{ ...s.td, textAlign: "center" }}>{diffBadge(r.dEv)}</td>
                        <td style={{ ...s.td, textAlign: "center", color: "#d97706", fontWeight: 500 }}>{r.real.draw_pct}%</td>
                        <td style={{ ...s.td, textAlign: "center", color: "#9ca3af" }}>{r.model.ber}%</td>
                        <td style={{ ...s.td, textAlign: "center" }}>{diffBadge(r.dBer)}</td>
                        <td style={{ ...s.td, textAlign: "center", color: "#2563eb", fontWeight: 500 }}>{r.real.away_pct}%</td>
                        <td style={{ ...s.td, textAlign: "center", color: "#9ca3af" }}>{r.model.dep}%</td>
                        <td style={{ ...s.td, textAlign: "center" }}>{diffBadge(r.dDep)}</td>
                        <td style={{ ...s.td, textAlign: "center" }}>{diffBadge(r.dGol)}</td>
                        <td style={{ ...s.td, textAlign: "center" }}>
                          {r.needsUpdate
                            ? <span style={{ background: "#fef2f2", color: "#dc2626", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>⚠ Güncelle</span>
                            : <span style={{ background: "#f0fdf4", color: "#16a34a", padding: "2px 8px", borderRadius: 4, fontSize: 11 }}>✓ OK</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Config satırı üreteci */}
            {updateNeeded.length > 0 && (
              <div style={s.card}>
                <div style={s.cardHd}>
                  <span>🔧 config.py Güncelleme Satırları</span>
                  <button
                    onClick={() => {
                      const text = updateNeeded.map(generateConfigLine).join("\n");
                      navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
                    }}
                    style={{ fontSize: 12, padding: "3px 12px", borderRadius: 4, border: "1px solid #2563eb", background: copied ? "#2563eb" : "#fff", color: copied ? "#fff" : "#2563eb", cursor: "pointer", fontWeight: 500 }}
                  >
                    {copied ? "✓ Kopyalandı" : "📋 Tümünü Kopyala"}
                  </button>
                </div>
                <div style={{ padding: 14, background: "#1e1e1e", borderRadius: "0 0 8px 8px", overflowX: "auto" }}>
                  <pre style={{ margin: 0, fontSize: 12, color: "#d4d4d4", fontFamily: "monospace", lineHeight: 1.6 }}>
                    {updateNeeded.map(r => (
                      <div key={r.league}>
                        <span style={{ color: "#6a9955" }}># {r.league}: ev Δ{r.dEv > 0 ? "+" : ""}{r.dEv}, ber Δ{r.dBer > 0 ? "+" : ""}{r.dBer}, dep Δ{r.dDep > 0 ? "+" : ""}{r.dDep}</span>{"\n"}
                        <span style={{ color: "#ce9178" }}>{generateConfigLine(r)}</span>{"\n"}
                      </div>
                    ))}
                  </pre>
                </div>
              </div>
            )}

            {/* Motivasyon haritası için uyarı — baseline değişti mi? */}
            <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 6, padding: "10px 16px", fontSize: 12, color: "#166534" }}>
              💡 <strong>Baseline güncellenirse:</strong> Motivasyon ayarlamalarını (LEAGUE_MOTIVATION) da kontrol et.
              Baseline büyük değiştiyse motivasyon pp'leri de yeniden hesaplanmalı.
              Şu an {Object.keys(MOT_DATA).length} lig için motivasyon verisi mevcut.
            </div>
          </div>
        );
      })()}

      {/* ════ Motivasyon ════ */}
      {ksTab === "motivasyon" && (
        <div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
            {MOT_LIGS.map(l => (
              <button key={l} onClick={() => setMotLig(l)} style={s.ligBtn(motLig === l)}>{l}</button>
            ))}
          </div>
          <MotTable rows={MOT_DATA[motLig].ev} title="Ev sahibi durumu" />
          <MotTable rows={MOT_DATA[motLig].dep} title="Deplasman durumu" />
        </div>
      )}

      {/* Market Edge */}
      {ksTab === "market" && (
        <div style={s.card}>
          <div style={s.cardHd}>
            <span>Lig bazlı market edge özeti</span>
            <span style={{ fontSize: 11, background: "#eff6ff", color: "#1d4ed8", padding: "2px 8px", borderRadius: 4, fontWeight: 500 }}>14 lig kalibre</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
              <thead>
                <tr>
                  {["Lig","Maç","EDGE-A (büyük ev favorisi)","EDGE-B (deplasman galibiyeti)","EDGE-C (beraberlik)","Uygulanan ayar","En güçlü sinyal"].map((h, i) => (
                    <th key={i} style={{ ...s.th, width: ["18%","7%","13%","13%","13%","16%","20%"][i] }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MKT_DATA.map((r, i) => (
                  <tr key={i} style={{ background: i % 2 === 1 ? "#f9fafb" : "#fff" }}>
                    <td style={{ ...s.td, fontWeight: 500 }}>{r.l}</td>
                    <td style={{ ...s.td, color: "#6b7280" }}>{r.n.toLocaleString()}</td>
                    <td style={s.td}>{badgeTxt(r.a)}</td>
                    <td style={s.td}>{badgeTxt(r.b)}</td>
                    <td style={s.td}>{badgeTxt(r.c)}</td>
                    <td style={{ ...s.td, color: "#6b7280", fontSize: 11 }}>{r.app}</td>
                    <td style={{ ...s.td, color: "#6b7280", fontSize: 11 }}>{r.sig}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dönem / Midweek */}
      {ksTab === "donem" && (
        <div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 10 }}>
            AR-004 · 14 lig · 24.265 maç · Sezon 5 eşit dilime bölündü (D1 = başlangıç, D5 = bitiş)
          </div>
          <div style={s.card}>
            <div style={s.cardHd}><span>Dönemsel ev avantajı sapması (gerçek% - piyasa%, pp)</span></div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
                <thead>
                  <tr>
                    {["Lig","D1 (baş)","D2","D3 (orta)","D4","D5 (bitiş)","Midweek ev","Midweek ber","En güçlü bulgu"].map((h, i) => (
                      <th key={i} style={{ ...s.th, width: ["16%","8%","6%","8%","6%","8%","9%","9%","30%"][i] }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DONEM_DATA.map((r, i) => (
                    <tr key={i} style={{ background: i % 2 === 1 ? "#f9fafb" : "#fff" }}>
                      <td style={{ ...s.td, fontWeight: 500 }}>{r.l}</td>
                      {r.d.map((v, j) => { const n = parseFloat(v); const abs = Math.abs(n);
                        const col = abs < 1.5 ? null : n > 0 ? (abs >= 5 ? { bg: "#fffbeb", txt: "#92400e" } : { bg: "#f0fdf4", txt: "#166534" }) : { bg: "#fef2f2", txt: "#991b1b" };
                        return <td key={j} style={s.td}>{col ? <span style={{ background: col.bg, color: col.txt, padding: "2px 6px", borderRadius: 4, fontWeight: 500, fontSize: 11 }}>{v}</span> : <span style={{ color: "#9ca3af" }}>{v}</span>}</td>;
                      })}
                      {[r.mw, r.mwb].map((v, j) => { const n = parseFloat(v); const abs = Math.abs(n);
                        const col = abs < 1.5 ? null : n > 0 ? (abs >= 5 ? { bg: "#fffbeb", txt: "#92400e" } : { bg: "#f0fdf4", txt: "#166534" }) : { bg: "#fef2f2", txt: "#991b1b" };
                        return <td key={j} style={s.td}>{col ? <span style={{ background: col.bg, color: col.txt, padding: "2px 6px", borderRadius: 4, fontWeight: 500, fontSize: 11 }}>{v}</span> : <span style={{ color: "#9ca3af" }}>{v}</span>}</td>;
                      })}
                      <td style={{ ...s.td, color: "#6b7280", fontSize: 11 }}>{r.k}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Takım Sapmaları */}
      {ksTab === "takim" && (
        <div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 10 }}>
            AR-004 · Takım bazlı sapma — minimum 25 maç, |sapma| ≥ 4pp · 406 takım analiz edildi
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            {[
              { title: "Piyasanın en çok hafife aldığı ev sahipleri", badge: { bg: "#f0fdf4", txt: "#166534", lbl: "gerçek > piyasa" }, rows: TAKIM_POS, pos: true },
              { title: "Piyasanın en çok aşırı fiyatladığı ev sahipleri", badge: { bg: "#fef2f2", txt: "#991b1b", lbl: "gerçek < piyasa" }, rows: TAKIM_NEG, pos: false },
            ].map(({ title, badge: b, rows, pos }) => (
              <div key={title} style={s.card}>
                <div style={s.cardHd}>
                  <span style={{ fontSize: 12 }}>{title}</span>
                  <span style={{ fontSize: 11, background: b.bg, color: b.txt, padding: "2px 8px", borderRadius: 4, fontWeight: 500 }}>{b.lbl}</span>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead><tr>
                    <th style={s.th}>Takım</th><th style={s.th}>Lig</th>
                    <th style={{ ...s.th, width: 44 }}>Maç</th>
                    <th style={{ ...s.th, width: 72 }}>Sapma</th>
                  </tr></thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const n = parseFloat(r.e);
                      const abs = Math.abs(n);
                      const col = pos ? (abs >= 17 ? { bg: "#fffbeb", txt: "#92400e" } : { bg: "#f0fdf4", txt: "#166534" })
                                      : { bg: "#fef2f2", txt: "#991b1b" };
                      return (
                        <tr key={i} style={{ background: i % 2 === 1 ? "#f9fafb" : "#fff" }}>
                          <td style={{ ...s.td, fontWeight: 500 }}>{r.t}</td>
                          <td style={{ ...s.td, color: "#6b7280", fontSize: 11 }}>{r.l}</td>
                          <td style={s.td}>{r.n}</td>
                          <td style={s.td}><span style={{ background: col.bg, color: col.txt, padding: "2px 7px", borderRadius: 4, fontWeight: 500, fontSize: 11 }}>{r.e}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
  );
}
